import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!smsConsent) {
      toast.error('You must agree to receive SMS messages to continue. Required for booking confirmations.');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      const result = await signup({
        ...userData,
        sms_consent: true,
        sms_consent_at: new Date().toISOString(),
      });

      if (result.success) {
        toast.success('Account created successfully!');
        navigate('/');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Signup failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    background: 'rgba(10,10,10,0.88)',
    border: '1px solid rgba(170,255,0,0.18)',
    borderRadius: 'var(--ss-radius-md)',
    color: 'var(--ss-white)',
    fontSize: '1rem',
    outline: 'none',
  };

  const fields = [
    { id: 'name', label: 'Full Name', type: 'text', placeholder: 'John Doe' },
    { id: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com' },
    { id: 'phone', label: 'Phone', type: 'tel', placeholder: '(239) 555-0123' },
    { id: 'password', label: 'Password', type: 'password', placeholder: '••••••••' },
    { id: 'confirmPassword', label: 'Confirm Password', type: 'password', placeholder: '••••••••' },
  ];

  return (
    <div className="sauce-sity-page" data-testid="signup-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div className="sauce-sity-content" style={{ maxWidth: 480, width: '100%' }}>
        <div className="ss-card ss-card-purple" data-testid="signup-card">
          <div className="ss-text-center ss-mb-24">
            <img
              src="https://customer-assets.emergentagent.com/job_barber-cape-coral/artifacts/b9xk2g3p_761.png"
              alt="Sauce Sity Logo"
              style={{ height: 112, width: 112, objectFit: 'contain', margin: '0 auto 12px' }}
            />
            <h1 className="ss-title-lg">Create <span className="ss-highlight-purple">Account</span></h1>
            <p className="ss-text">Join Sauce Sity and book your next appointment</p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {fields.map((f) => (
              <div key={f.id}>
                <label htmlFor={f.id} style={{ display: 'block', color: 'var(--ss-white)', fontWeight: 600, marginBottom: 6 }}>{f.label}</label>
                <input
                  id={f.id}
                  name={f.id}
                  data-testid={`signup-${f.id}-input`}
                  type={f.type}
                  placeholder={f.placeholder}
                  value={formData[f.id]}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                />
              </div>
            ))}

            {/* SMS opt-in consent — required for 10DLC / TFN compliance */}
            <div
              style={{
                background: 'rgba(170,255,0,0.05)',
                border: '1px solid rgba(170,255,0,0.25)',
                borderRadius: 12,
                padding: 14,
                marginTop: 4,
              }}
              data-testid="sms-consent-block"
            >
              <label
                htmlFor="sms-consent"
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', color: 'var(--ss-white)' }}
              >
                <input
                  id="sms-consent"
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  required
                  data-testid="signup-sms-consent-checkbox"
                  style={{
                    width: 20, height: 20, flexShrink: 0, marginTop: 2,
                    accentColor: 'var(--ss-green)', cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: '0.86rem', lineHeight: 1.45 }}>
                  I agree to receive SMS appointment confirmations, reminders, and promotional offers
                  from Sauce Sity Barbershop at the phone number above. Message frequency varies
                  (typically 2–4 per month). <strong>Message and data rates may apply.</strong> Reply{' '}
                  <strong>STOP</strong> to opt out, reply <strong>HELP</strong> for help. View our{' '}
                  <Link to="/terms-conditions" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ss-green)', textDecoration: 'underline' }}>
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ss-green)', textDecoration: 'underline' }}>
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            </div>

            <button
              type="submit"
              data-testid="signup-submit-btn"
              disabled={loading || !smsConsent}
              className="ss-btn ss-btn-primary ss-btn-full"
              style={{ opacity: (loading || !smsConsent) ? 0.6 : 1, cursor: (loading || !smsConsent) ? 'not-allowed' : 'pointer', marginTop: 8 }}
            >
              {loading ? 'Creating Account…' : 'Create Account'}
            </button>
          </form>

          <div className="ss-text-center ss-mt-24">
            <p className="ss-text">
              Already have an account?{' '}
              <Link to="/login" data-testid="signup-login-link" style={{ color: 'var(--ss-green)', fontWeight: 700, textDecoration: 'none' }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
